import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Paper, Typography, IconButton, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTheme } from "@mui/material/styles";
import { getTextColor } from '../../../utils/colorUtils';
import SimulationTable from "../../simulations/SimulationTable";
import SimulationCollectionTable from "../../sim-collections/SimulationCollectionTable";
import CourseTable from "../../courses/CourseTable";
import ContentPackTable from "../../content-packs/ContentPackTable";
import { useGetContentPacksQuery } from "../../../store/services/university-service";

ContentSelection.propTypes = {
    format: PropTypes.string.isRequired,
    simsAndCoursesToSend: PropTypes.array.isRequired,
    setSimsAndCoursesToSend: PropTypes.func.isRequired,
    onNextStep: PropTypes.func.isRequired,
    isRandomMode: PropTypes.bool.isRequired,
    onSwitchToRandomMode: PropTypes.func.isRequired,
    onSwitchToSpecificMode: PropTypes.func.isRequired
};

export default function ContentSelection(props) {
    const {
        format,
        simsAndCoursesToSend,
        setSimsAndCoursesToSend,
        onNextStep,
        isRandomMode,
        onSwitchToRandomMode,
        onSwitchToSpecificMode
    } = props;

    const { tenancyId } = useSelector((state) => state.tenancy);
    const theme = useTheme();

    // State for content pack pagination
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 100,
        page: 0
    });

    // State for simulation collection banner
    const [showSimCollectionBanner, setShowSimCollectionBanner] = useState(true);

    // Fetch content packs when format is contentpack
    const {
        data: contentPacks,
        isLoading: contentPacksLoading,
        error: contentPacksError,
        refetch: refetchContentPacks
    } = useGetContentPacksQuery({
        perPage: paginationModel.pageSize,
        page: paginationModel.page,
        searchTerm: '',
        tenancyId: tenancyId
    }, {
        skip: format !== "contentpack"
    });

    // Ensure simsAndCoursesToSend is always an array
    const selectedIds = useMemo(() => Array.isArray(simsAndCoursesToSend) ? simsAndCoursesToSend : [], [simsAndCoursesToSend]);

    const handleRandomSelect = useCallback(() => {
        if (format === "simulation") {
            // Switch to random mode instead of proceeding directly
            onSwitchToRandomMode();
        } else {
            // For other formats, proceed as before
            onNextStep();
        }
    }, [format, onSwitchToRandomMode, onNextStep]);

    const handleSpecificSelect = useCallback(() => {
        if (format === "simulation") {
            // Switch to random mode instead of proceeding directly
            onSwitchToSpecificMode();
        } else {
            // For other formats, proceed as before
            onNextStep();
        }
    }, [format, onSwitchToSpecificMode, onNextStep]);

    const handleSetSelected = useCallback((newSelected) => {
        // Ensure we're always setting an array
        setSimsAndCoursesToSend(Array.isArray(newSelected) ? newSelected : []);
    }, [setSimsAndCoursesToSend]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", width: "100%" }}>
            {format === "simulation" && !isRandomMode && (
                <SimulationTable
                    type="select"
                    simulationIds={selectedIds}
                    setSimulationId={handleSetSelected}
                    onRandomSelect={handleRandomSelect}
                />
            )}

            {format === "simulation" && isRandomMode && (
                <>
                    {showSimCollectionBanner && (
                        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: theme.palette.primary.main, color: getTextColor(theme.palette.primary.main), display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '8px' }}>
                            <Box>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <Typography variant="h6">
                                        Randomised Simulation Sending
                                    </Typography>
                                    <Tooltip title="The system automatically tracks user history to keep simulations fresh and prevent repetition">
                                        <IconButton size="small" sx={{ color: 'inherit', ml: 1 }}>
                                            <HelpOutlineIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography variant="body2">
                                   When you send a simulation collection, each recipient will receive a randomly chosen simulation from the selected collection.
                                </Typography>
                            </Box>
                            <IconButton size="small" sx={{ color: 'inherit' }} onClick={() => setShowSimCollectionBanner(false)}>
                                <CancelIcon />
                            </IconButton>
                        </Paper>
                    )}
                    <SimulationCollectionTable
                        type="select"
                        collectionIds={selectedIds}
                        setCollectionId={handleSetSelected}
                        onSpecificSelect={handleSpecificSelect}
                    />
                </>
            )}

            {format === "course" && (
                <CourseTable
                    type="select"
                    courseIds={selectedIds}
                    setCourseId={handleSetSelected}
                    onRandomSelect={handleRandomSelect}
                />
            )}

            {format === "contentpack" && (
                <ContentPackTable
                    type="select"
                    contentPackIds={selectedIds}
                    setContentPackId={handleSetSelected}
                    onRandomSelect={handleRandomSelect}
                    contentPackData={contentPacks || []}
                    isLoading={contentPacksLoading}
                    error={contentPacksError}
                    refetchContentPacks={refetchContentPacks}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                />
            )}
        </Box>
    );
}